(() => {
  if (customElements.get('sc-collection-controls')) return;

  class SCCollectionControls extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-ref="sidebar-toggle"]');
      this.overlay = this.querySelector('[data-ref="sidebar-overlay"]');
      this.panel = this.querySelector('[data-ref="sidebar-menu"]');
      this.menu = this.querySelector('side-menu-component');
      this.onClick = (event) => {
        if (!event.target.closest('[data-sc-collection-close]')) return;
        if (typeof this.menu?.close === 'function') this.menu.close();
        else this.querySelector('[data-ref="sidebar-close"]')?.click();
      };
      this.onPopState = () => this.syncSort();
      this.onUpdated = () => {
        this.sync();
        this.restoreFocus();
      };
      this.onFiltersChange = () => this.rememberFocus(document.activeElement);
      this.onFocusIn = (event) => {
        if (this.pendingFocus) this.rememberFocus(event.target);
      };
      this.onKeyDown = (event) => {
        if (event.key !== 'Tab' || !this.isOpen()) return;
        const controls = [...this.panel.querySelectorAll('a[href], button, input, select, textarea, [tabindex="0"]')]
          .filter(control => !control.disabled && !control.hidden && control.getClientRects().length > 0);
        const first = controls[0];
        const last = controls.at(-1);
        if (!first) {
          event.preventDefault();
          this.panel.focus();
        } else if (event.shiftKey && (document.activeElement === first || !this.panel.contains(document.activeElement))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !this.panel.contains(document.activeElement))) {
          event.preventDefault();
          first.focus();
        }
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onFiltersChange, true);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('filters:updated', this.onUpdated);
      window.addEventListener('popstate', this.onPopState);
      document.addEventListener('keydown', this.onKeyDown);
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      this.sync();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onFiltersChange, true);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('filters:updated', this.onUpdated);
      window.removeEventListener('popstate', this.onPopState);
      document.removeEventListener('keydown', this.onKeyDown);
      this.observer?.disconnect();
      this.pendingFocus = null;
      if (this.overlay?.classList.contains('is-open')) {
        this.overlay.classList.remove('is-open', 'is-closing');
        if (!document.querySelector('.header__mobile-menu-overlay.is-open')) document.body.classList.remove('overflow-hidden');
      }
    }

    isOpen() {
      return this.overlay?.classList.contains('is-open') && !this.overlay.classList.contains('is-closing');
    }

    syncSort() {
      const select = this.querySelector('[data-ref="sorting-select"]');
      if (!select) return;
      const wanted = new URL(window.location.href).searchParams.get('sort_by') || this.dataset.defaultSort;
      if ([...select.options].some(option => option.value === wanted)) select.value = wanted;
    }

    rememberFocus(control) {
      const form = this.querySelector('[data-ref="filters-form"]');
      if (!this.isOpen() || !form?.contains(control) || !control.matches('input, select, textarea')) {
        this.pendingFocus = null;
        return;
      }
      this.pendingFocus = {
        element: control,
        id: control.getAttribute('id'),
        name: control.getAttribute('name'),
        value: control.value,
        scrollTop: form.scrollTop
      };
    }

    restoreFocus() {
      const pending = this.pendingFocus;
      this.pendingFocus = null;
      if (!pending || !this.isOpen()) return;
      const active = document.activeElement;
      if (active && active !== document.body && active !== pending.element) return;
      const form = this.querySelector('[data-ref="filters-form"]');
      if (!form) return;
      let control = [...form.querySelectorAll('[id]')].find(item => pending.id && item.getAttribute('id') === pending.id);
      if (!control && pending.name) {
        control = [...form.querySelectorAll('[name]')].find(item => item.getAttribute('name') === pending.name && item.value === pending.value);
      }
      form.scrollTop = pending.scrollTop;
      if (control && !control.disabled && !control.hidden) control.focus({ preventScroll: true });
      else this.querySelector('[data-ref="sidebar-close"]')?.focus();
    }

    sync() {
      this.syncSort();
      if (!this.isOpen()) this.pendingFocus = null;
      this.toggle?.setAttribute('aria-expanded', String(Boolean(this.isOpen())));
      const count = this.querySelector('[data-sc-active-count]')?.dataset.scActiveCount || '0';
      const badge = this.querySelector('[data-sc-filter-count]');
      if (badge) {
        if (badge.textContent !== count) badge.textContent = count;
        badge.hidden = Number(count) === 0;
      }
    }
  }

  customElements.define('sc-collection-controls', SCCollectionControls);
})();

if (!customElements.get('sc-collection-breadcrumb')) {
  customElements.define('sc-collection-breadcrumb', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = new AbortController();
      this.current = this.querySelector('[data-sc-current-crumb]');
      if (!this.current) return;
      this.media = window.matchMedia('(max-width: 750px)');
      this.expanded = false;
      this.dataset.expanded = 'false';
      this.current.setAttribute('aria-expanded', 'false');
      const options = { signal: this.controller.signal };
      this.addEventListener('click', event => {
        if (!this.media.matches || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
        const crumb = event.target.closest('[data-sc-crumb]');
        if (crumb === this.current && (this.expanded || this.hasOverflow)) {
          this.setExpanded(!this.expanded);
        } else if (crumb && !this.expanded && crumb.scrollWidth > crumb.clientWidth + 1) {
          event.preventDefault();
          this.setExpanded(true);
        }
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || !this.expanded) return;
        event.preventDefault();
        this.setExpanded(false);
        this.current.focus({ preventScroll: true });
      }, options);
      this.media.addEventListener('change', () => {
        if (!this.media.matches) {
          if (document.activeElement === this.current) [...this.querySelectorAll('a[data-sc-crumb]')].at(-1)?.focus({ preventScroll: true });
          this.setExpanded(false);
        } else {
          this.checkOverflow();
        }
      }, options);
      this.observer = new ResizeObserver(() => this.checkOverflow());
      this.observer.observe(this);
      requestAnimationFrame(() => this.checkOverflow());
      document.fonts?.ready.then(() => { if (this.isConnected) this.checkOverflow(); });
    }

    checkOverflow() {
      this.hasOverflow = this.media.matches && [...this.querySelectorAll('[data-sc-crumb]')].some(crumb => crumb.scrollWidth > crumb.clientWidth + 1);
      this.dataset.overflow = String(this.hasOverflow);
      this.current.disabled = !this.media.matches || !this.expanded && !this.hasOverflow;
    }

    setExpanded(expanded) {
      this.expanded = expanded;
      this.dataset.expanded = String(expanded);
      this.current.setAttribute('aria-expanded', String(expanded));
      this.checkOverflow();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }
  });
}
